import { z } from "zod";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";

import { CardsSwapping } from "@/components/animations/cards-swapping";
import { Brand } from "@/components/ui/brand";
import { OrderFulfillmentModal } from "@/components/modals/order-fulfillment";

import { useUserOnboarded } from "@/hooks/use-onboarded";
import { useAuthenticationStore } from "@/store/authentication";
import { BuyPlansModal } from "@/components/modals/buy-plans";
import { useQueryClient } from "@tanstack/react-query";
import { SubscriptionApiFactory } from "@/apis/subscription";

export const Route = createFileRoute("/auth/_layout")({
  validateSearch: z.object({
    next: z.string().nullish(),
    email: z.string().nullish(),
    plans: z.boolean().nullish(),
    payment: z.string().nullish(),
    session_id: z.string().nullish(),
  }),
  component: RouteLayout,
});

function RouteLayout() {
  const params = Route.useSearch();
  const navigate = Route.useNavigate();

  const client = useQueryClient();
  const onboarded = useUserOnboarded();
  const user = useAuthenticationStore((store) => store.user);

  useEffect(() => {
    if (user) {
      navigate({ to: onboarded ? params.next || "/dashboard" : "/onboarding" });
    }
  }, [user, onboarded, params.next, navigate]);

  useEffect(() => {
    client.prefetchQuery(SubscriptionApiFactory.Queries.FetchAllPlans());
  }, [client]);

  const handleTogglePlansModal = useCallback(
    (open: boolean) => {
      navigate({
        to: ".",
        search: {
          plans: open,
        },
      });
    },
    [navigate]
  );

  const handleCloseOrderModal = useCallback(
    (status: "success" | "failed", email: string) => {
      switch (status) {
        case "success":
          navigate({ to: ".", search: { session_id: undefined, email } });
          break;
        case "failed":
          navigate({ to: ".", search: { session_id: undefined, plans: true } });
          break;
      }
    },
    [navigate]
  );

  return (
    <main className="grid grid-cols-1 lg:grid-cols-2 h-[100dvh] relative">
      <Brand className="absolute top-4 left-4 lg:top-8 lg:left-8">
        <Brand.Icon />
        <Brand.Text />
      </Brand>
      <section className="px-4 lg:px-8 py-16 flex flex-col items-center justify-center">
        <div className="max-w-md w-full">
          <Outlet />
          {params.email ? <BuyPlansModal email={params.email} open={!!params.plans} onOpenChange={handleTogglePlansModal} /> : null}
          {params.session_id ? <OrderFulfillmentModal session_id={params.session_id} open={!!params.session_id} onClose={handleCloseOrderModal} /> : null}
        </div>
      </section>
      <CardsSwapping />
    </main>
  );
}
